import { ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

type StatColor = "primary" | "secondary" | "green" | "amber" | "red" | "muted";

const colorClasses: Record<StatColor, string> = {
  primary: "bg-primary/10 text-primary",
  secondary: "bg-secondary/10 text-secondary-foreground",
  green: "bg-green-500/10 text-green-600 dark:text-green-400",
  amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  red: "bg-red-500/10 text-red-600 dark:text-red-400",
  muted: "bg-muted text-muted-foreground",
};

const shadowColors: Record<StatColor, { shadow: string; border: string }> = {
  primary: {
    shadow: "color-mix(in oklab, var(--primary) 30%, transparent)",
    border: "color-mix(in oklab, var(--primary) 45%, transparent)",
  },
  secondary: {
    shadow: "color-mix(in oklab, var(--secondary) 30%, transparent)",
    border: "color-mix(in oklab, var(--secondary) 45%, transparent)",
  },
  green: {
    shadow: "color-mix(in oklab, var(--success) 30%, transparent)",
    border: "color-mix(in oklab, var(--success) 45%, transparent)",
  },
  amber: {
    shadow: "color-mix(in oklab, var(--warning) 30%, transparent)",
    border: "color-mix(in oklab, var(--warning) 45%, transparent)",
  },
  red: {
    shadow: "color-mix(in oklab, var(--destructive) 30%, transparent)",
    border: "color-mix(in oklab, var(--destructive) 45%, transparent)",
  },
  muted: {
    shadow: "color-mix(in oklab, var(--muted-foreground) 25%, transparent)",
    border: "color-mix(in oklab, var(--muted-foreground) 40%, transparent)",
  },
};

interface StatCardProps {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  color?: StatColor;
}

export function StatCard({ icon, label, value, sub, color = "primary" }: StatCardProps) {
  const styles = {
    "--shadow-color": shadowColors[color].shadow,
    "--border-color": shadowColors[color].border,
  } as React.CSSProperties;

  return (
    <Card className="stat-card-hover group cursor-pointer" style={styles}>
      <CardContent className="p-4 flex items-start gap-3">
        <div
          className={cn(
            "h-10 w-10 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110",
            colorClasses[color],
          )}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground font-medium">{label}</p>
          <p className="text-lg font-bold truncate stat-number">{value}</p>
          {sub && <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}
